// In dev this is "/" — src/setupProxy.js forwards /api to staging so the browser sees a
// single origin, which is what makes the SameSite=Lax session cookie attach on localhost.
export const BASE_URL = process.env.REACT_APP_API_URL;

// Absolute backend origin. BASE_URL is relative in dev, so anything that must be a real
// public URL (webhooks, third-party redirects) has to use this instead.
export const API_ORIGIN = BASE_URL?.startsWith("http")
  ? BASE_URL.replace(/\/$/, "")
  : "https://staging.backend-app.usedeex.com"; // dev proxy target

// Use this rather than BASE_URL?.includes("staging") — BASE_URL is "/" in dev, which would
// otherwise read as production and e.g. fire live analytics or pick the prod Sudo vault.
export const IS_STAGING = API_ORIGIN.includes("staging");

// NB: the matching callback URL is also registered in the Smile Identity dashboard —
// changing this alone is not enough.
export const SMILE_URL = `${API_ORIGIN.replace(
  /^https?:\/\//,
  ""
)}/webhooks/smileid`;

export const API = {
  // auth
  login: `${BASE_URL}api/auth/signin`,
  signup: `${BASE_URL}api/auth/signup`,
  forgotPassword: `${BASE_URL}api/auth/forgot-password`,
  resetPassword: `${BASE_URL}api/auth/reset-password`,
  createPassword: `${BASE_URL}api/auth/create-password`,
  verifyEmail: `${BASE_URL}api/users/me/complete-email-verification`,
  resendOTP: `${BASE_URL}api/users/me/resend-otp`,
  createPin: `${BASE_URL}api/auth/create-pin`,
  changePin: `${BASE_URL}api/auth/change-pin`,
  completeLogin: `${BASE_URL}api/auth/complete-signin`,
  logout: `${BASE_URL}api/auth/logout`,
  deleteAccount: `${BASE_URL}api/auth/account`,

  // user
  getUser: `${BASE_URL}api/users/me`,
  verifyKyc:`${BASE_URL}api/users/me/kyc/smile-id-bvn`,
  survey: `${BASE_URL}api/users/me/survey`,
  phone: `${BASE_URL}api/users/me/phone`,
  profile: `${BASE_URL}api/users/me`,
  getBanks: `${BASE_URL}api/banks/palmpay`,
  getBanksDeex: `${BASE_URL}api/banks`,
  generateAccountStatement: `${BASE_URL}api/users/me/account-statement`,
  verifyPhone: `${BASE_URL}api/users/me/phone/send-otp`,
  verifyPhoneOtp: `${BASE_URL}api/users/me/phone/verify`,
  makeDefaultBank: (bankId: string) =>
    `${BASE_URL}api/users/me/bank-details/${bankId}/make-default`,
  deleteBank: (bankId: string) =>
    `${BASE_URL}api/users/me/bank-details/${bankId}`,
  verifyAccountNumber: (accountNumber: string, bankCode: string) =>
    `${BASE_URL}api/banks/account-name/palmpay?accountNumber=${accountNumber}&bankCode=${bankCode}`,
  saveAccountDetails: `${BASE_URL}api/users/me/bank-details`,
  getSummary: `${BASE_URL}api/users/me/summary`,
  changePassword: `${BASE_URL}api/users/me/password`,
  requestOTP: `${BASE_URL}api/users/me/request-otp`,
  checkUsername: (username: string) =>
    `${BASE_URL}api/users/username/${username}/availability`,
  addUsername: `${BASE_URL}api/users/me/username`,
  savePushToken: `${BASE_URL}api/users/me/push-tokens`,
  kycLevel2: `${BASE_URL}api/users/me/kyc/bvn`,
  verifyKycLevel2: `${BASE_URL}api/users/me/kyc/bvn/verify-otp`,
    addDOB: `${BASE_URL}api/users/me/dob`,
  getKycDetails: `${BASE_URL}api/users/me/kyc`,
  getLimits: `${BASE_URL}api/users/me/limits`,
  // notification
  getNotifications: (page: number) =>
    `${BASE_URL}api/notifications/in-app?page=${page}&limit=10`,

  //gift-cards
  getGiftCards: `${BASE_URL}api/gift-cards?status=active`,
  getGiftCardDetails: (id: string) => `${BASE_URL}api/gift-cards/${id}`,
  createGiftCardTransaction: `${BASE_URL}api/gift-cards-transactions`,
  requestGiftCardImageUpload: `${BASE_URL}api/gift-cards-transactions/images/request-upload-url`,

  //crypto
  getCryptoWallet: (coin: string) => `${BASE_URL}api/users/me/wallets/${coin}`,

  // transactions — server-side filtered, cursor-paginated.
  // Spec: deexoptions-mobile-app/docs/activity-system.md
  // Query strings are always built by buildTransactionsQuery / buildSummaryQuery
  // (utils/activity/filters.ts) — never hand-assemble params here. Note BASE_URL
  // already carries its trailing slash, so these must not start with one.
  getTransactions: (query: string) => `${BASE_URL}api/transactions/me?${query}`,
  getTransactionsSummary: (query: string) =>
    `${BASE_URL}api/transactions/me/summary?${query}`,
  // Mixed-type feed: takes `limit` and an optional `coin`; no type/subtype.
  getRecentTransactions: (query: string) =>
    `${BASE_URL}api/transactions/me/recent?${query}`,

  //settings
  toggle2Fa: (active: boolean) =>
    `${BASE_URL}api/users/me/account/settings/2fa/${
      active ? "activate" : "deactivate"
    }`,
  requestQrcode: `${BASE_URL}api/users/me/account/settings/2fa-auth-app/request-qrcode`,
  activateGoogleAuth: `${BASE_URL}api/users/me/account/settings/2fa-auth-app/activate`,
  deactivateGoogleAuth: `${BASE_URL}api/users/me/account/settings/2fa-auth-app/deactivate`,

  //in app
  getRates: `${BASE_URL}api/rates`,
  getTickers: `${BASE_URL}api/rates/markets/tickers`,

  //deex assets
  requestWallets: `${BASE_URL}api/deex-assets/wallets/request-crypto-wallets`,
  requestNgnWallets: `${BASE_URL}api/deex-assets/wallets/request-fiat-wallets`,
  getWallets: `${BASE_URL}api/deex-assets/wallets`,
  executeSwapping: `${BASE_URL}api/deex-assets/swaps/execute-instant-order`,
  executeSwappingFromNgn: `${BASE_URL}api/deex-assets/swaps/from-ngn`,
  executeSwappingToNgn: `${BASE_URL}api/deex-assets/swaps/to-ngn`,
  requestSwappingQuote: `${BASE_URL}api/deex-assets/swaps/create-instant-order`,
  coinCurrentValue: (coin = "") =>
    `${BASE_URL}api/rates/crypto-values/${coin}`,
  withdrawAsset: `${BASE_URL}api/deex-assets/request-withdrawal`,
  withdrawToDeexAsset: `${BASE_URL}api/deex-assets/request-deex-withdrawal/`,
  sellCrypto: `${BASE_URL}api/deex-assets/sell-crypto`,
  requestNairaDeposit: `${BASE_URL}api/deex-assets/request-naira-deposit-bank`,
  getNairaWalletStatus: `${BASE_URL}api/naira-wallet/status`,
  getNairaWallet: `${BASE_URL}api/naira-wallet`,
  createNairaWallet: `${BASE_URL}api/naira-wallet`,
  withdrawNairaWallet: `${BASE_URL}api/naira-wallet/withdraw`,
  getNairaWalletTransactions: (page: number, limit = 10) =>
    `${BASE_URL}api/naira-wallet/transactions?page=${page}&limit=${limit}`,
  requestWithdrawalFee: (network: string, currency: string) =>
    `${BASE_URL}api/deex-assets/withdrawal-fee?network=${network}&currency=${currency}`,
  publicWalletDetails: `${BASE_URL}api/wallets/public/details`,

  getDeexTags: (search: string) =>
    `${BASE_URL}api/users/verified?limit=10&page=1&search=${search}`,
  getTansferLimit: `${BASE_URL}api/deex-assets/send-money/limit`,
  transferToBank: `${BASE_URL}api/deex-assets/send-money`,
  //reward
  getFplManagers: `${BASE_URL}api/tasks/fpl/manager-of-the-week-history`,
  getTasks: `${BASE_URL}api/tasks`,
  getStreak: `${BASE_URL}api/users/me/streak`,
  redeemPoints: `${BASE_URL}api/tasks/redeem-points`,
  getReferralHistory: `${BASE_URL}api/tasks/points/activity`,
  getPointsBalance: `${BASE_URL}api/tasks/points/summary`,
  applyTask: (id: string) => `${BASE_URL}api/tasks/${id}/apply`,
  getTradeStreaks: (filter: string) =>
    `${BASE_URL}api/tasks/trade-streaks${filter}`,
  applyTradeStreak: (id: string) =>
    `${BASE_URL}api/tasks/trade-streaks/${id}/claim-reward`,
  getPointsActivity: `${BASE_URL}api/tasks/points/activity`,
  getSignupStats: `${BASE_URL}api/tasks/signup-bonus-status`,
  previewInviteCode: (code: string) =>
    `${BASE_URL}api/invite-codes/${encodeURIComponent(code)}/preview`,
  redeemInviteCode: `${BASE_URL}api/invite-codes/redeem`,
  getJoinedInviteCodes: `${BASE_URL}api/invite-codes/joined`,
  getCashbacks: `${BASE_URL}api/cashback/user/current`,
  claimCashback: (id: string) => `${BASE_URL}api/cashback/user/claim/${id}`,
  //hostcap
  migrateToHostCap: `${BASE_URL}api/users/me/mirgrate/host-cap`,
  getConversionRate: (from:string, to:string, amount:number) => `${BASE_URL}api/deex-assets/swaps/request-quote?fromAsset=${from}&toAsset=${to}&amount=${amount}`,
executeSwap: `${BASE_URL}api/deex-assets/swaps/execute`,
  //utilities
  getAirtimeProviders: `${BASE_URL}api/utilities/airtime/providers`,
  buyAirtime: `${BASE_URL}api/utilities/airtime`,
  getDataPlans: `${BASE_URL}api/utilities/data/plans`,
  buyData: `${BASE_URL}api/utilities/data`,
  buyElectricity: `${BASE_URL}api/utilities/electricity`,
  getElectricityProviders: `${BASE_URL}api/utilities/electricity/providers`,
  electricityVerify: `${BASE_URL}api/utilities/electricity/verify`,
  getBettingProviders:`${BASE_URL}api/utilities/betting/providers`,
  fundAza:`${BASE_URL}api/utilities/betting/fund`,
  validateCustomer:(betId:string, customerId:string)=>`${BASE_URL}api/utilities/betting/validate/${betId}/${customerId}`,

  //advance notification
  getAdvNotiff:(status:string, type:string)=>`${BASE_URL}api/notifications/advanced?status=${status}&type=${type}`,

  // surveys (user-facing; the admin half lives under /api/admin/surveys).
  // Not to be confused with `survey` above, which is the older one-off
  // users/me/survey endpoint and is unrelated to this system.
  activeSurvey: (placement: string) =>
    `${BASE_URL}api/surveys/active?placement=${placement}`,
  recordSurveyView: (id: string) => `${BASE_URL}api/surveys/${id}/view`,
  dismissSurvey: (id: string) => `${BASE_URL}api/surveys/${id}/dismiss`,
  startSurveyResponse: (id: string) => `${BASE_URL}api/surveys/${id}/responses`,
  saveSurveyResponse: (id: string, responseId: string) =>
    `${BASE_URL}api/surveys/${id}/responses/${responseId}`,

  //kyc3
  getKyc3UploadURL:`${BASE_URL}api/users/me/kyc/3/request-upload-url`,
  submitKyc3:`${BASE_URL}api/users/me/kyc/3`,

  //kyc4 (Level 3)
  getKyc4UploadURL: `${BASE_URL}api/users/me/kyc/4/request-upload-url`,
  submitKyc4: `${BASE_URL}api/users/me/kyc/4`,

  //cards
  getCards: `${BASE_URL}api/virtual-card`,
  createCard: `${BASE_URL}api/virtual-card`,
  getCardDetails: (id: string) => `${BASE_URL}api/virtual-card/${id}`,
  getCardToken: (cardId: string) =>
    `${BASE_URL}api/virtual-card/token/${cardId}`,
  deleteCard: (id: string) => `${BASE_URL}api/virtual-card/terminate/${id}`,
  freezeCard: (id: string) => `${BASE_URL}api/virtual-card/freeze/${id}`,
  unfreezeCard: (id: string) => `${BASE_URL}api/virtual-card/unfreeze/${id}`,
  topUpCard: (id: string) => `${BASE_URL}api/virtual-card/topup/${id}`,
  withdrawCard: (id: string) => `${BASE_URL}api/virtual-card/withdraw/${id}`,
  getCardTransactions: (id: string, page: number, limit: number, startDate: string, endDate: string) =>
    `${BASE_URL}api/virtual-card/transactions/${id}?start_date=${startDate}&end_date=${endDate}&page_size=${limit}&page=${page}`,
  generateCardStatement: (id: string) => `${BASE_URL}api/virtual-card/transactions/${id}/card-statement`,

  //oauth
  initiateLogin: ( provider: string) => `${BASE_URL}api/auth/web/${provider}`,

  //tracking
  trackEvent: `${BASE_URL}api/users/track`,

  //beneficiary
  saveBeneficiary: `${BASE_URL}api/beneficiaries/crypto`,
  deleteBeneficiary: (id: string) => `${BASE_URL}api/beneficiaries/${id}`,
getBeneficiaries: `${BASE_URL}api/beneficiaries?type=crypto`,

//wrapped
getWrappedDeet: `${BASE_URL}api/users/me/year-in-review`
};





